import Image from 'next/legacy/image';
import Link from 'next/link';
import { FaRocket } from 'react-icons/fa';
import TruckCharlotte from '../../assets/home-tmp/truck-charlotte.png';
import TruckSideways from '../../assets/home-tmp/truck-sideways.png';
import { Meta } from '../../components/meta/meta';

export default function Home() {
  return (
    <main className="relative">
      <Meta />
      {/* Header */}
      <section className="to-fold relative w-full overflow-hidden">
        <Image
          alt="Beds for Kids Truck"
          src={TruckCharlotte}
          layout="fill"
          objectFit="cover"
          className="z-0 brightness-50"
          quality={100}
          loading="eager"
          priority
        />
        <div className="relative mx-auto mt-[10vh] max-w-2xl md:mt-[30vh]">
          <h1 className="mx-4 text-center text-3xl font-bold text-white">
            Providing Beds <br className="md:hidden" />
            &amp; Essential Furniture
            <br className="md:hidden" /> to Children
            <br className="md:hidden" /> &amp; Their Families In Need
          </h1>
        </div>
        <div className="relative mx-auto mt-[25vh] max-w-4xl text-white md:mt-[25vh]">
          <p className="text-center font-semibold">choose an amount to donate:</p>
          <div className="mt-3 grid grid-cols-2 gap-4 px-4 md:grid-cols-4">
            <HomeDonateButton amount={720} text="fully furnish a home" />
            <HomeDonateButton amount={200} text="provide a twin bed set" />
            <HomeDonateButton amount={25} text="provide a bed kit" />
            <Link
              href={`/donate`}
              className="flex items-center justify-center rounded-lg border-2 border-white bg-green-500 bg-opacity-80 p-2">
              <p className="text-center text-xl leading-4">
                other
                <br />
                amount
              </p>
            </Link>
          </div>
          <section className="m-3">
            <p className="text-center text-xs text-gray-400">
              Secure Payment &bull; This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" className="text-green-400 hover:underline">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href="https://policies.google.com/terms" className="text-green-400 hover:underline">
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </section>
        </div>
      </section>
      {/* Fold */}
      <section className="mx-auto my-8 max-w-3xl px-4 text-blue-500">
        <h2 className="relative text-center text-3xl md:text-6xl">
          furnishing futures<sup className="-top-3 text-lg md:-top-7 md:text-2xl">TM</sup>
        </h2>
        <p className="mt-4 text-center leading-5">
          The mission of Beds for Kids is to provide beds and essential furniture to children and their families in
          need. Beds for Kids exists to provide a critical need in Charlotte that would otherwise be unmet. Our
          organization started in 2011 with an individual wanting to donate a gently used sofa to someone in need. They
          were given a family&apos;s name from a church, and while they were delivering this sofa, they realized the
          rest of the home was empty. After being unable to forget the emptiness in the home, they began to research the
          continuum of housing services that existed in Mecklenburg County and found that one of the largest gaps was an
          organization committed to serving families impacted by furniture poverty. This group ended up pulling together
          resources to serve the rest of the items the family needed - beds, kitchen table, chairs and other essential
          items. This first delivery inspired the creation of Beds for Kids, but the smiles and excitement of over 5,000
          families keep us motivated to serve each and every day.
        </p>
      </section>
      <section className="mx-auto my-16 grid max-w-3xl gap-8 px-4 text-blue-500 md:grid-cols-2">
        <Image src={TruckSideways} alt="Beds for Kids truck being unloaded" className="rounded-lg" placeholder="blur" />
        <div>
          <h2 className="relative text-center text-2xl md:text-5xl">
            not <span className="italic text-green-500">just</span> beds
          </h2>
          <p className="mt-4 text-center leading-5">
            Our organization consists of a small full-time staff, but a dedicated volunteer base of over 2000 volunteers
            each year. We are completely dependent on the generosity of individuals in the Charlotte area who donate
            their Time, Furniture, and Funds. It has been incredible to see people find specific ways to leverage what
            they are passionate about to help further our mission. Whether it be something as large as donating a
            warehouse, lamp fixing expertise, or even donating a new pillow, our organization is a product of these
            differing donations coming together.
          </p>
        </div>
      </section>
      <section className="mx-auto my-16 -mt-12 grid max-w-3xl items-center gap-8 px-4 text-blue-500 md:grid-cols-2">
        <div>
          <h2 className="relative text-center text-2xl md:text-5xl">more soon!</h2>
          <p className="mt-4 text-center leading-5">
            Beds for Kids is currently hard at work creating a brand new website and expanding our online presence. Stay
            tuned for more very soon!
          </p>
        </div>
        <FaRocket className="h-full w-full p-16" />
      </section>
    </main>
  );
}

export function HomeDonateButton({ amount, text }: { amount: number; text: string }) {
  return (
    <Link
      href={`/donate?amount=${amount}`}
      className="rounded-lg border-2 border-white bg-green-500 bg-opacity-80 p-2 text-center">
      <p className="text-3xl font-semibold">
        <sup className="text-lg">$</sup>
        {amount}
      </p>
      <p className="text-xs text-gray-300">{text}</p>
    </Link>
  );
}
